import { normalize } from 'normalizr';
import { showLoading, hideLoading } from 'react-redux-loading-bar';
import isObject from 'lodash/isObject';
import get from 'lodash/get';
import map from 'lodash/map';
import moment from 'moment';
import pipe from 'ramda/src/pipe';
import ramdaMap from 'ramda/src/map';
import tap from 'ramda/src/tap';
import {
  ADD_CUSTOMER,
  ADD_CUSTOMER_PHOTO,
  DELETE_CUSTOMER,
  IMPORT_CUSTOMERS,
  IMPORT_CUSTOMER_APPOINTMENTS,
  IMPORT_LETTERS,
  IMPORT_REWARD_COUNTER,
  IMPORT_SINGLE_CUSTOMER,
  LOAD_CUSTOMER_PER_PAGE,
  SELECT_LETTER,
  UPDATE_CUSTOMER,
  GET_CUSTOMER_APPOINTMENT,
  CLEAR_CUSTOMER_APPOINTMENTS,
} from './constants';
import {
  SET_CUSTOMERS_LOADED,
  LOADING_STARTED,
  CONFIRMATION_REQUIRED_STATUS,
} from '../constants';
import { extractAppointments, formatAppointment } from '../../common/helpers/appointments';
import Http from '../../http';
import config from '../../../config';
import * as schema from './schema';
import confirmModal from '../../common/confirmModal';


function transform(customer) {
  if (isObject(customer)) {
    customer.birthDate = customer.birthDate
      ? moment.parseZone(customer.birthDate).format(config.dateFormat)
      : undefined;
  }
  return customer;
}

const fetchCustomers = (endpoint, filters = {}) => dispatch =>
  Http.get(endpoint, filters).then((data) => {
    const customers = map(data.list, transform);
    return dispatch({
      type: IMPORT_CUSTOMERS,
      response: normalize(customers, schema.arrayOfCustomers),
      pagination: data.pagination,
    });
  });

function getCustomers(endpoint, page) {
  const filter = {
    include: 'photos,user',
    sortBy: 'firstName,lastName',
    limit: LOAD_CUSTOMER_PER_PAGE,
    page,
  };
  return async (dispatch) => {
    dispatch({ type: LOADING_STARTED });
    dispatch(showLoading());
    await dispatch(fetchCustomers(endpoint, filter));
    dispatch(hideLoading());
    return dispatch({ type: SET_CUSTOMERS_LOADED });
  };
}

export function getOwnerCustomers(ownerId, ownerType, letter = 'A', page = 1) {
  const endpoint = `/${ownerType}/${ownerId}/customer/letter/${letter}`;
  return getCustomers(endpoint, page);
}

const fetchLetters = endpoint => dispatch =>
  Http.get(endpoint)
    .then(data => dispatch({
      type: IMPORT_LETTERS,
      payload: data,
    }));

export function fetchOwnerLetters(ownerId, ownerType) {
  const endpoint = `/${ownerType}/${ownerId}/customer/letters`;
  return fetchLetters(endpoint);
}

export function getCustomersByField(shopId, filterBy, searchText) {
  const filter = {
    limit: 10,
  };
  if (filterBy === 'name') {
    filter.or = true;
    filter.firstName__like = searchText;
    filter.lastName__like = searchText;
    filter.sortBy = 'firstName,lastName';
  } else {
    filter[`${filterBy}__like`] = searchText;
    filter.sortBy = `${filterBy}`;
  }

  return () => Http.get(`/shop/${shopId}/customer`, filter).then((data) => {
    const customers = map(data, transform);
    return customers;
  });
}

function addCustomer(endpoint, data) {
  return dispatch => Http.post(endpoint, data).then((newCustomer) => {
    const customer = transform(newCustomer);
    dispatch({
      type: ADD_CUSTOMER,
      response: normalize(customer, schema.customer),
    });
    return customer;
  });
}

export function addOwnerCustomer(ownerId, ownerType, data) {
  const endpoint = `/${ownerType}/${ownerId}/customer`;
  return addCustomer(endpoint, data);
}

function getCustomer(endpoint, include = '') {
  return dispatch => Http.get(endpoint, { include })
      .then((fetchedCustomer) => {
        const customer = transform(fetchedCustomer);
        dispatch({
          type: IMPORT_SINGLE_CUSTOMER,
          response: normalize(customer, schema.customer),
        });
        return customer;
      });
}

export function getOwnerCustomer(ownerId, ownerType, customerId) {
  const endpoint = `/${ownerType}/${ownerId}/customer/${customerId}`;

  return getCustomer(endpoint, 'photos,user,blockedShops,blockedBrands,blockedBarbers');
}

export function changeOwnerCustomerBlockStatus(ownerId, ownerType, customerId, status) {
  const endpoint = `/customer/${customerId}/${status}`;
  const payload = { [`${ownerType}Id`]: ownerId };

  return Http.post(endpoint, payload);
}

const getCustomerRewardCounter = endpoint => dispatch =>
  Http.get(endpoint, { include: 'reward' })
    .then(reward => dispatch({
      type: IMPORT_REWARD_COUNTER,
      payload: {
        count: get(reward, 'countsToGoal', 0),
        type: get(reward, 'reward.rewardType'),
        lastRewardCodeUsed: get(reward, 'lastRewardCodeUsed'),
        unusedRewardCodes: get(reward, 'unusedRewardCodes', []),
      },
    }));

export const getOwnerCustomerRewardCounter = (ownerId, ownerType, customerId) =>
  getCustomerRewardCounter(`/${ownerType}/${ownerId}/reward/customer/${customerId}`);

export function updateOwnerCustomer(ownerId, ownerType, customerId, data) {
  return dispatch => Http.put(`/${ownerType}/${ownerId}/customer/${customerId}`, data).then((updatedCustomer) => {
    const customer = transform(updatedCustomer);
    dispatch({
      type: UPDATE_CUSTOMER,
      response: normalize(customer, schema.customer),
    });
    return customer;
  });
}

export function deleteOwnerCustomer(ownerId, ownerType, customerId, options = { deleteSubscriptionConfirm: 'ask' }) {
  const payload = {
    confirmations: {
      deleteSubscription: options.deleteSubscriptionConfirm,
    },
  };

  return dispatch => Http.delete(`/${ownerType}/${ownerId}/customer/${customerId}`, payload).then(({ ok }) => {
    if (ok) {
      dispatch({ type: DELETE_CUSTOMER, customerId });
      return true;
    }

    return false;
  }).catch(async (err) => {
    if (err.status === CONFIRMATION_REQUIRED_STATUS) {
      const confirmed = await confirmModal({
        title: 'Heads up!',
        message: err.message,
        type: 'yesno',
        confirmButtonText: 'Yes',
        negativeButtonText: 'No',
      });

      if (confirmed) {
        return dispatch(deleteOwnerCustomer(ownerId, ownerType, customerId, {
          deleteSubscriptionConfirm: 'yes',
        }));
      }
    }

    throw err;
  });
}

export function addCustomerImage(customerId, image) {
  return dispatch => Http.postRaw(`/customer/${customerId}/photo`, {
    image,
    enabled: true,
    order: 0,
  }).then((photo) => {
    dispatch({
      type: ADD_CUSTOMER_PHOTO,
      photo,
      customerId,
    });
    return photo;
  });
}

export function changeCustomerImage(customerId, oldImage, newImage) {
  return (dispatch, getState) => Http.delete(`/customer/${customerId}/photo/${oldImage.id}`)
    .then(() => addCustomerImage(customerId, newImage)(dispatch, getState));
}

const importCustomerAppointmentsAction = appointments => ({
  type: IMPORT_CUSTOMER_APPOINTMENTS,
  appointments,
});

const appointmentInclude = [
  'barber',
  'customer',
  'shop',
  'appointment_service',
  'payment',
  'promos.customer_promo.promo',
  'bookedBy',
].join(',');
export function getCustomerAppointments(shopId, customerId, barberUser = null) {
  const query = {
    customerId,
    shopId,
    include: appointmentInclude,
    sortBy: 'dateTime:desc',
    limit: 30,
  };
  if (barberUser) {
    query.shopId = barberUser.shopId;
    query.barberId = barberUser.id;
  }
  return dispatch => Http.get('/appointment', query)
    .then(pipe(extractAppointments, importCustomerAppointmentsAction, dispatch));
}

export const selectLetter = letter => ({
  type: SELECT_LETTER,
  letter,
});

export const clearCustomerAppointments = () => ({ type: CLEAR_CUSTOMER_APPOINTMENTS });

const fetchCustomerAppointments = url => (dispatch) => {
  const mapItem = item => ({
    ...item,
    barberFullName: item.barberName,
    start: item.dateTime,
    end: item.endTime,
    description: item.items,
  });
  const transformItems = ramdaMap(mapItem);
  return Http.get(url).then(pipe(transformItems, importCustomerAppointmentsAction, dispatch));
};

export const fetchOwnerCustomerAppointments = (ownerId, ownerType, customerId) =>
  fetchCustomerAppointments(`/${ownerType}/${ownerId}/customer/${customerId}/appointment`);

const getAppointmentAction = appointment => ({
  type: GET_CUSTOMER_APPOINTMENT,
  appointment,
});

export const getAppointment = appointmentId => dispatch =>
  Http.get(`/appointment/${appointmentId}`, { include: appointmentInclude })
    .then(pipe(
      formatAppointment,
      tap(pipe(getAppointmentAction, dispatch))
    ));

